// apps/share-web/src/player-view/chart-transport.ts
//
// Транспорт полноэкранного чтения: Play, полоса позиции, время.
//
// Пункт приёмки A1-5(б) — регресс против прототипа, который Сергей уже принимал:
// «в мобильной версии мы же делали там, типа, внизу, чтобы кнопка Play была как в
// прототипе. Почему мы от этого отошли?» В прототипе (блок `.fb` в
// prototype-live.html) низ слоя нёс круглую Play, полосу позиции и время; волна A
// заменила этот ряд навигацией по песням. На телефоне слой — ЕДИНСТВЕННАЯ
// поверхность, и читатель со стойки перестал мочь поставить на паузу, не выйдя
// из чтения.
//
// ПОЧЕМУ НЕ ЦЕЛИКОМ `<bw-transport>` через player-view/transport.ts. Разбирался,
// и он сюда не подходит по трём измеримым причинам, а не по вкусу:
//   1. полосы позиции у него НЕТ вовсе (bw-transport.ts: ряд кнопок, время с
//      бар-счётчиком, мастер-фейдер) — а прототип и спека просят именно
//      «кнопку Play, полосу позиции и время»;
//   2. его ряд кнопок несёт prev/next по сетлисту, и они дублировали бы
//      парящие «‹ ›» слоя (`.cw-fab` в `.cw-body`, words page v2; до неё — ряд `.cw-fs-nav`);
//   3. он несёт мастер-громкость и кнопку записи (промо десктопа) — на
//      поверхности для чтения со стойки это шум, и он стоит вертикали, которой
//      чтению и не хватает (замер живьём: `.pv-transport` — 168 px высоты).
//
// Что при этом НЕ делается своими руками — сама Play. Она ДС-компонент
// `bw-play-button`: правило пакета «never hand-roll UI that duplicates a bw-*
// component», и вторая самодельная круглая кнопка разъехалась бы с транспортом
// плеера первым же изменением дизайн-системы. Полоса позиции своя, потому что
// такой атомарной единицы в ДС нет вовсе: `bw-slider` — это LED-фейдер микшера,
// `bw-volume-bar` — громкость с VU, а десктоп позицию рисует волной. Нативный
// range при этом выигрывает у любой самодельной полоски: он доступен с
// клавиатуры и умеет палец из коробки.
//
// Времени и позиции модуль НЕ добывает: их приносит `sync` из кадра слоя, а тот
// берёт их из того же `PositionSource`, которым живёт ведение листа. Второй
// источник времени на одной поверхности разъехался бы с первым.

import '@bandway/ui-components';
import { t } from '../ui/i18n.js';

export interface ChartTransportDeps {
  /** Длительность песни, сек — шкала полосы. Ноль значит «неизвестна». */
  durationSec: number;
  /** Играть/пауза. Решение «что именно» принадлежит владельцу плеера. */
  toggle: () => void;
  /** Перемотка на позицию в секундах. */
  seek: (posSec: number) => void;
  /** Песня ещё грузится — ДС-кнопка сама покажет спиннер и запретит нажатие. */
  isLoading?: () => boolean;
}

export interface ChartTransportHandle {
  /** Один кадр: где песня и играет ли. Источник — общий с ведением. */
  sync(posSec: number, playing: boolean): void;
  destroy(): void;
}

/** `m:ss`. Тот же формат, что у транспорта плеера (transport.ts). */
const fmt = (sec: number): string => {
  const s = Math.max(0, Math.floor(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

type PlayEl = HTMLElement & { playing: boolean; loading: boolean; size: string };

export function mountChartTransport(host: HTMLElement, deps: ChartTransportDeps): ChartTransportHandle {
  const row = document.createElement('div');
  row.className = 'cw-fs-tr';

  const play = document.createElement('bw-play-button') as PlayEl;
  play.size = 'lg';
  play.playing = false;
  play.loading = false;

  const seek = document.createElement('input');
  seek.className = 'cw-fs-seek';
  seek.type = 'range';
  seek.min = '0';
  // Длительность может быть неизвестна (0): шкала тогда вырождается, поэтому
  // полоса выключается — двигать нечего, и ползунок, прыгающий по нулевой шкале,
  // врал бы о положении в песне.
  seek.max = String(deps.durationSec > 0 ? deps.durationSec : 0);
  seek.step = '0.01';
  seek.value = '0';
  seek.disabled = !(deps.durationSec > 0);
  seek.setAttribute('aria-label', t('chart.seek'));

  const time = document.createElement('div');
  time.className = 'cw-fs-time';

  row.appendChild(play);
  row.appendChild(seek);
  row.appendChild(time);
  host.appendChild(row);

  /**
   * Заливка полосы. Пройденная часть рисуется фоном самого input'а через
   * `--cw-seek-pos` (0…1) — chart.css читает её в градиенте. Ставится ЗДЕСЬ, а не
   * в CSS: нативный range своего «сколько пройдено» стилям не отдаёт вовсе, и
   * это ровно то, что в прототипе делал `.prog i`.
   */
  const paint = (shownSec: number): void => {
    const frac = deps.durationSec > 0
      ? Math.max(0, Math.min(1, shownSec / deps.durationSec))
      : 0;
    seek.style.setProperty('--cw-seek-pos', String(frac));
  };
  paint(0);

  const onPlay = (): void => { deps.toggle(); };
  play.addEventListener('bw-play-click', onPlay);

  // Пока палец (или стрелка на клавиатуре) ведёт полосу, кадр её не отбирает:
  // плеер ещё стоит на старой позиции, и синхронизация утаскивала бы ползунок
  // назад под пальцем. `input` начинает ведение, `change` его заканчивает —
  // это и мышь, и палец, и клавиатура одинаково.
  let seeking = false;
  const onSeekInput = (): void => {
    seeking = true;
    paint(Number(seek.value));
    deps.seek(Number(seek.value));
  };
  const onSeekChange = (): void => {
    seeking = false;
    paint(Number(seek.value));
    deps.seek(Number(seek.value));
  };
  seek.addEventListener('input', onSeekInput);
  seek.addEventListener('change', onSeekChange);

  const sync = (posSec: number, playing: boolean): void => {
    play.playing = playing;
    play.loading = deps.isLoading?.() ?? false;
    time.textContent = deps.durationSec > 0
      ? `${fmt(posSec)} / ${fmt(deps.durationSec)}`
      : fmt(posSec);
    if (seeking) return;
    const shown = Math.max(0, Math.min(deps.durationSec, posSec));
    seek.value = String(shown);
    paint(shown);
  };

  sync(0, false);

  return {
    sync,
    destroy() {
      play.removeEventListener('bw-play-click', onPlay);
      seek.removeEventListener('input', onSeekInput);
      seek.removeEventListener('change', onSeekChange);
      row.remove();
    },
  };
}
